// Encrypted media is stored as ciphertext on the server. This hook fetches
// it (the service worker caches the ciphertext), checks its hash, decrypts it
// and returns an object URL; decrypted URLs are memoised per file.
import { useEffect, useState } from "preact/hooks";
import type { Media } from "../db";


const cache = new Map<string, Promise<string>>();
const MAX = 150;

export function decryptedURL(m: Media): Promise<string> {
  const k = m.url! + "#" + m.key;
  let p = cache.get(k);
  if (!p) {
    p = (async () => {
      const res = await fetch(m.url!);
      if (!res.ok) throw new Error(`media ${res.status}`);
      const { E2E } = await import("../e2e/e2e");
      const blob = await E2E.decryptBlob(await res.arrayBuffer(), m.key!, m.iv!, m.mime, m.sha256);
      return URL.createObjectURL(blob);
    })();
    p.catch(() => cache.delete(k));
    cache.set(k, p);
    if (cache.size > MAX) {
      const [oldKey, old] = cache.entries().next().value!;
      cache.delete(oldKey);
      void old.then((u) => URL.revokeObjectURL(u), () => {});
    }
  }
  return p;
}

/** Save a (possibly encrypted) file to the user's device. */
export async function downloadMedia(m: Media) {
  const url = m.key ? await decryptedURL(m) : m.url + "?download";
  const a = document.createElement("a");
  a.href = url;
  a.download = m.name ?? "file";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** A URL that can be put in <img>/<video>/<audio>/<a download>. */
export function useMediaSrc(m: Media | undefined, enabled = true): { src?: string; error?: string } {
  const [state, setState] = useState<{ src?: string; error?: string }>({});
  const needsDecrypt = !!(m?.url && m.key && m.iv);
  useEffect(() => {
    if (!m || !enabled) return;
    if (!m.url && m.blob) {
      const u = URL.createObjectURL(m.blob);
      setState({ src: u });
      // The <img> may still show it while the uploaded copy decrypts.
      return () => setTimeout(() => URL.revokeObjectURL(u), 10_000);
    }
    if (!needsDecrypt) {
      setState({ src: m.url });
      return;
    }
    let live = true;
    decryptedURL(m).then(
      (src) => live && setState({ src }),
      (e) => live && setState({ error: e instanceof Error ? e.message : String(e) }),
    );
    return () => {
      live = false;
    };
  }, [m?.url, m?.key, m?.blob, enabled]);
  return state;
}
